// UI-only mock data. 12 solver profiles with reshaped "problems solved".
// Problem shape: { title, description, outcome, tools, categories, dateSolved }

export const CATEGORIES = [
  "AI Basics & Getting Started",
  "ChatGPT & General AI Assistance",
  "Prompt Engineering",
  "AI Research & Applied ML",
  "RAG & Search",
  "MCP & Integrations",
  "AI Agents & Automation",
  "Workflow Automation",
  "Evals & Reliability",
  "AI Coding & DevTools",
  "AI Product Design",
  "AI Infrastructure & Deployment",
  "APIs & Model Integration",
  "Fine-Tuning & Custom Models",
  "Data & Analysis",
  "Data Extraction & Processing",
  "Knowledge Bases & Enterprise Search",
  "Voice AI & Conversational AI",
  "Generative Media",
  "Content & Creative AI",
  "AI Design & Prototyping",
  "Website & App Building",
  "AI for Marketing",
  "AI for Sales",
  "AI for Customer Support",
  "AI for Operations",
  "AI for Productivity",
  "AI for Business & Strategy",
  "AI for Teaching & Learning",
  "AI for Recruiting & HR",
  "AI for Finance & Accounting",
  "AI for Legal & Compliance",
  "AI for Ecommerce",
  "AI for Social Media",
  "AI for SEO",
  "AI Security & Privacy",
  "Model Selection & Comparison",
  "Model Context & Memory",
  "Structured Outputs & Tool Use",
  "Multi-Agent Systems",
];

// AI Models — displayed to users as "AI Models". Flat list, alphabetised
// loosely by provider then chronology.
export const AI_MODELS = [
  // OpenAI
  "GPT-5.6 Sol", "GPT-5.6 Terra", "GPT-5.6 Luna",
  "GPT-5.5", "GPT-5.4", "GPT-5.3 Codex", "GPT-5.2", "GPT-5.1",
  "GPT-5", "GPT-5 mini",
  "GPT-4.1", "GPT-4o", "GPT-4o mini",
  "o1", "o3", "o3-mini", "o4-mini",
  "Codex", "GPT Image", "Whisper", "OpenAI text-embedding",
  // Anthropic
  "Claude Fable 5", "Claude Mythos 5", "Claude Opus 5", "Claude Sonnet 5",
  "Claude Opus 4.8", "Claude Opus 4.7", "Claude Opus 4.6", "Claude Opus 4.5",
  "Claude Sonnet 4.6",
  "Claude 3.7 Sonnet", "Claude 3.5 Sonnet", "Claude 3 Opus", "Claude 3 Haiku",
  // Google
  "Gemini 3 Pro", "Gemini 3 Flash", "Gemini 3.1 Flash Live",
  "Gemini 2.5 Pro", "Gemini 2.5 Flash", "Gemini Flash-Lite", "Gemini Live",
  "Imagen", "Veo", "Gemma", "CodeGemma", "PaliGemma",
  // xAI
  "Grok 4.5", "Grok 4.3", "Grok 4.20 Multi-Agent", "Grok Build 0.1",
  "Grok 4", "Grok 3", "Grok Imagine",
  // Meta
  "Llama 4", "Llama 3.3", "Llama 3.2", "Llama 3.1",
  "Code Llama", "Llama Guard",
  // Mistral
  "Mistral Large 3", "Mistral Medium 3.5", "Mistral Small 4", "Ministral 3",
  "Codestral", "Magistral", "Voxtral", "Pixtral", "Devstral",
  "Mistral Nemo", "Mixtral",
  // DeepSeek
  "DeepSeek V4 Pro", "DeepSeek V4 Flash", "DeepSeek V4 Preview",
  "DeepSeek V3.2", "DeepSeek V3", "DeepSeek R1", "DeepSeek R1-Distill",
  "DeepSeek Coder", "DeepSeek VL",
];

// Legacy export kept to avoid breaking imports elsewhere in the codebase.
// Points at AI_MODELS since the two concepts merged during the v11 rebrand.
export const AI_TOOLS = AI_MODELS;

export const AI_SOFTWARE_TOOLS = [
  "Cursor", "Windsurf", "GitHub Copilot", "Claude Code", "OpenAI Codex",
  "Gemini CLI", "Jules", "Qwen Code", "Grok Build", "Devin",
  "Replit", "Replit Agent", "Lovable", "Bolt", "Emergent",
  "v0", "Base44", "Firebase Studio", "Aider", "Cline",
  "Roo Code", "Continue", "OpenHands", "Augment Code", "Sourcegraph Cody",
  "Tabnine", "Amazon Q Developer", "Blackbox AI", "Zed", "CodeRabbit",
  "Sweep", "Mutable", "Pieces", "Warp", "JetBrains AI Assistant",
  "JetBrains Junie", "Copilot Workspace", "Codeium", "GitLab Duo",
];

export const EXPERIENCE_LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"];

const mk = (id, patch) => ({ id, isDemo: true, ...patch });

export const PEOPLE = [
  mk("maya-chen", {
    name: "Maya Chen", role: "Founding Engineer", location: "San Francisco, CA",
    photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "Building AI-native developer tools. I love turning fuzzy problems into tight, testable prompts and reproducible pipelines.",
    categories: ["AI Coding & DevTools", "RAG & Search"],
    tools: [
      { name: "Claude", level: "Expert" },
      { name: "Cursor", level: "Expert" },
      { name: "GitHub Copilot", level: "Advanced" },
    ],
    problemsPosted: 1,
    problems: [
      {
        title: "Cut code review time by 62% with an AI reviewer",
        description: "Built a Claude-powered pre-review bot that annotates PRs with style, security and test-coverage suggestions before humans look at them.",
        outcome: "62% reduction in reviewer time; 3x faster PR merge cycle.",
        tools: ["Claude", "GitHub Copilot"],
        categories: ["AI Coding & DevTools", "Evals & Reliability"],
        dateSolved: "2025-11-14",
      },
      {
        title: "Turned 900 pages of legacy docs into a searchable Q&A",
        description: "Chunked & embedded internal docs, then wrapped them with a Claude retrieval prompt.",
        outcome: "New engineer onboarding time dropped from 3 weeks to 8 days.",
        tools: ["Claude"],
        categories: ["RAG & Search"],
        dateSolved: "2025-08-02",
      },
    ],
  }),
  mk("daniel-okafor", {
    name: "Daniel Okafor", role: "AI Product Manager", location: "London, UK",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "I turn AI capabilities into features people actually use. Ex-fintech, currently shipping copilots for SMB accounting teams.",
    categories: ["AI Product Design", "AI Agents & Automation"],
    tools: [
      { name: "ChatGPT", level: "Expert" },
      { name: "Perplexity", level: "Advanced" },
      { name: "Notion AI", level: "Advanced" },
    ],
    problemsPosted: 2,
    problems: [
      {
        title: "Shipped an AI copilot that automates 40% of month-end close",
        description: "Designed prompt patterns and human-in-the-loop checkpoints for a bookkeeping assistant that reconciles invoices, categorises expenses and drafts journal entries. Used by 3,200 SMBs.",
        outcome: "40% of close tasks now automated; NPS +18 among power users.",
        tools: ["ChatGPT", "Notion AI"],
        categories: ["AI Product Design", "AI Agents & Automation"],
        dateSolved: "2025-10-20",
      },
      {
        title: "Ran 27 customer discovery calls in 2 weeks with AI notes",
        description: "Recorded, transcribed and synthesised calls with Whisper + ChatGPT into a shared insight board.",
        outcome: "Discovery cycle time cut in half; team aligned on top 3 themes in one meeting.",
        tools: ["ChatGPT", "Whisper"],
        categories: ["Workflow Automation", "Content & Creative AI"],
        dateSolved: "2025-06-11",
      },
    ],
  }),
  mk("priya-shah", {
    name: "Priya Shah", role: "Growth Marketer", location: "Mumbai, IN",
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "SEO + performance marketing operator. I run scrappy AI-assisted content engines for B2B SaaS.",
    categories: ["Content & Creative AI", "Workflow Automation"],
    tools: [
      { name: "ChatGPT", level: "Expert" },
      { name: "Perplexity", level: "Expert" },
      { name: "Zapier AI", level: "Intermediate" },
    ],
    problemsPosted: 0,
    problems: [
      {
        title: "Scaled a B2B blog from 12 to 180 posts in 90 days",
        description: "Built a repeatable outline → draft → edit workflow using Perplexity for research and Claude for structure, with a strict human editor step.",
        outcome: "Organic sessions +340%; 6 posts ranking in top-3 on target keywords.",
        tools: ["ChatGPT", "Perplexity", "Claude"],
        categories: ["Content & Creative AI"],
        dateSolved: "2025-12-01",
      },
      {
        title: "Cut lead qualification time by 70%",
        description: "Zapier + GPT-driven enrichment scores every inbound lead against ICP fit before it hits sales.",
        outcome: "SDR capacity freed up; conversion to demo +22%.",
        tools: ["Zapier AI", "ChatGPT"],
        categories: ["Workflow Automation"],
        dateSolved: "2025-05-19",
      },
    ],
  }),
  mk("sofia-alvarez", {
    name: "Sofia Álvarez", role: "UX Designer", location: "Barcelona, ES",
    photo: "https://images.pexels.com/photos/8111893/pexels-photo-8111893.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bio: "Product designer with a soft spot for AI-native interfaces. I obsess over the space between a prompt and a person.",
    categories: ["AI Product Design", "Generative Media"],
    tools: [
      { name: "Midjourney", level: "Expert" },
      { name: "Nano Banana", level: "Advanced" },
      { name: "ChatGPT", level: "Advanced" },
    ],
    problemsPosted: 1,
    problems: [
      {
        title: "Prototyped an AI onboarding flow that lifted activation 34%",
        description: "Replaced a 6-step form with a friendly chat interface that asks only what's needed, backed by a small prompt library.",
        outcome: "Day-1 activation +34%; drop-off in step 3 eliminated.",
        tools: ["ChatGPT"],
        categories: ["AI Product Design"],
        dateSolved: "2025-09-22",
      },
      {
        title: "Generated 200+ concept visuals for a rebrand in a week",
        description: "Combined Midjourney and Nano Banana to explore identity directions before touching Figma.",
        outcome: "Client picked a direction in one workshop; saved ~3 weeks of design cycles.",
        tools: ["Midjourney", "Nano Banana"],
        categories: ["Generative Media"],
        dateSolved: "2025-07-08",
      },
    ],
  }),
  mk("amara-johnson", {
    name: "Amara Johnson", role: "Head of Operations", location: "Atlanta, GA",
    photo: "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bio: "I make companies run. I use AI to compress everything I hate: reports, meeting notes, and status updates.",
    categories: ["Workflow Automation", "AI Agents & Automation"],
    tools: [
      { name: "Notion AI", level: "Expert" },
      { name: "n8n", level: "Advanced" },
      { name: "Claude", level: "Advanced" },
    ],
    problemsPosted: 3,
    problems: [
      {
        title: "Automated a 4-hour weekly reporting ritual down to 12 minutes",
        description: "n8n pulls data from HubSpot, Stripe and Linear; Claude turns it into a narrative briefing every Monday.",
        outcome: "Saved ~16 hours/month across leadership. Zero missed reports.",
        tools: ["n8n", "Claude"],
        categories: ["Workflow Automation"],
        dateSolved: "2025-11-30",
      },
      {
        title: "Rewrote an entire ops handbook in a weekend",
        description: "Used Notion AI to transform disorganised Loom transcripts into a searchable 80-page playbook.",
        outcome: "Onboarding tickets down 55%; playbook now referenced weekly.",
        tools: ["Notion AI"],
        categories: ["Content & Creative AI"],
        dateSolved: "2025-04-15",
      },
    ],
  }),
  mk("kenji-tanaka", {
    name: "Kenji Tanaka", role: "AI Researcher", location: "Tokyo, JP",
    photo: "https://images.unsplash.com/photo-1506863530036-1efeddceb993?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGNhc3VhbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "Applied researcher focused on evals, small models, and quiet, useful AI.",
    categories: ["Evals & Reliability", "AI Research & Applied ML"],
    tools: [
      { name: "Claude", level: "Expert" },
      { name: "Gemini", level: "Advanced" },
      { name: "Perplexity", level: "Advanced" },
    ],
    problemsPosted: 0,
    problems: [
      {
        title: "Built an eval harness that caught 3 silent regressions",
        description: "Designed a lightweight test suite for prompt changes with side-by-side diffing and a golden set.",
        outcome: "Team now ships prompt updates with the same confidence as code.",
        tools: ["Claude"],
        categories: ["Evals & Reliability"],
        dateSolved: "2025-12-18",
      },
      {
        title: "Distilled a 70B model into a task-specific 7B",
        description: "Fine-tuned a small model on curated production traces for a classification task.",
        outcome: "Inference cost -90%; F1 within 2 points of the teacher.",
        tools: ["Claude"],
        categories: ["AI Research & Applied ML"],
        dateSolved: "2025-10-05",
      },
    ],
  }),
  mk("leah-morgan", {
    name: "Leah Morgan", role: "Customer Success Lead", location: "Remote (US)",
    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwaGVhZHNob3QlMjBkaXZlcnNlfGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "I run post-sales. AI took the busywork off my plate so I can actually talk to customers.",
    categories: ["Workflow Automation", "Content & Creative AI"],
    tools: [
      { name: "ChatGPT", level: "Advanced" },
      { name: "Zapier AI", level: "Advanced" },
      { name: "Notion AI", level: "Intermediate" },
    ],
    problemsPosted: 1,
    problems: [
      {
        title: "Reduced churn by 18% with proactive AI-drafted check-ins",
        description: "A weekly job pulls usage signals and drafts a personalised nudge email for each at-risk account.",
        outcome: "Gross churn -18% quarter-over-quarter.",
        tools: ["Zapier AI", "ChatGPT"],
        categories: ["Workflow Automation"],
        dateSolved: "2025-09-05",
      },
      {
        title: "Turned 6 hours of QBR prep into 45 minutes",
        description: "Templates + AI summarisation mean QBR decks now assemble themselves from live data.",
        outcome: "8x faster QBR prep; CSMs run 2x more sessions.",
        tools: ["Notion AI", "ChatGPT"],
        categories: ["Content & Creative AI"],
        dateSolved: "2025-07-24",
      },
    ],
  }),
  mk("julian-park", {
    name: "Julian Park", role: "Indie Hacker", location: "Seoul, KR",
    photo: "https://images.unsplash.com/photo-1517256673644-36ad11246d21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxjcmVhdGl2ZSUyMGNhc3VhbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "One-person software company. I ship weird little tools with AI in the loop and share what I learn.",
    categories: ["AI Coding & DevTools", "RAG & Search"],
    tools: [
      { name: "Cursor", level: "Expert" },
      { name: "Claude", level: "Advanced" },
      { name: "n8n", level: "Intermediate" },
    ],
    problemsPosted: 2,
    problems: [
      {
        title: "Shipped a $4k MRR micro-SaaS as a solo founder",
        description: "Wrote 70% of the code with Cursor + Claude; kept the human bits (design, pricing) manual.",
        outcome: "Launched in 6 weeks; hit $4k MRR month 3.",
        tools: ["Cursor", "Claude"],
        categories: ["AI Coding & DevTools"],
        dateSolved: "2025-11-01",
      },
      {
        title: "Rebuilt my personal knowledge base as an AI second brain",
        description: "Local-first notes with a private RAG index over 12 years of writing.",
        outcome: "Search time on old notes dropped from minutes to seconds.",
        tools: ["Claude"],
        categories: ["RAG & Search"],
        dateSolved: "2025-08-19",
      },
    ],
  }),
  mk("elena-ruiz", {
    name: "Elena Ruiz", role: "Content Strategist", location: "Mexico City, MX",
    photo: "https://images.unsplash.com/photo-1517462964-21fdcec3f25b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxjcmVhdGl2ZSUyMGNhc3VhbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "I ghostwrite for founders. I use AI to research faster and edit sharper, never to write instead of me.",
    categories: ["Content & Creative AI", "Prompt Engineering"],
    tools: [
      { name: "Claude", level: "Expert" },
      { name: "Perplexity", level: "Expert" },
      { name: "ChatGPT", level: "Advanced" },
    ],
    problemsPosted: 0,
    problems: [
      {
        title: "Built a research kit that cuts pre-writing time in half",
        description: "Perplexity spaces + a custom Claude prompt distill 30 sources into an interview-ready brief.",
        outcome: "Pre-writing time -50%; clients get first drafts 2 days sooner.",
        tools: ["Perplexity", "Claude"],
        categories: ["Content & Creative AI"],
        dateSolved: "2025-10-11",
      },
      {
        title: "Trained a client-voice model on 4 years of their essays",
        description: "Assembled a private style guide + few-shot library so drafts sound like them, not like a chatbot.",
        outcome: "Client-approved first drafts jumped from ~40% to ~85%.",
        tools: ["Claude"],
        categories: ["Prompt Engineering"],
        dateSolved: "2025-06-30",
      },
    ],
  }),
  mk("noor-hassan", {
    name: "Noor Hassan", role: "Prompt Engineer", location: "Dubai, UAE",
    photo: "https://images.pexels.com/photos/18331031/pexels-photo-18331031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bio: "Half linguist, half engineer. I design prompts that survive real-world users.",
    categories: ["Prompt Engineering", "Evals & Reliability"],
    tools: [
      { name: "Claude", level: "Expert" },
      { name: "ChatGPT", level: "Expert" },
      { name: "Gemini", level: "Advanced" },
    ],
    problemsPosted: 1,
    problems: [
      {
        title: "Reduced hallucinations in a support bot by 78%",
        description: "Introduced a strict tool-first policy, refusal patterns and structured outputs with schema validation.",
        outcome: "Hallucination rate -78%; CSAT +11 points.",
        tools: ["Claude", "ChatGPT"],
        categories: ["Prompt Engineering", "Evals & Reliability"],
        dateSolved: "2025-12-05",
      },
      {
        title: "Cross-model prompt library shared across a 40-person team",
        description: "Versioned prompts, tested against evals, ported cleanly between Claude and GPT.",
        outcome: "Prompt reuse across the org went from ~0 to a shared library of 120 prompts.",
        tools: ["Claude", "ChatGPT", "Gemini"],
        categories: ["Prompt Engineering"],
        dateSolved: "2025-07-18",
      },
    ],
  }),
  mk("james-wright", {
    name: "James Wright", role: "Educator", location: "Melbourne, AU",
    photo: "https://images.unsplash.com/photo-1581841064838-a470c740e8ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxjcmVhdGl2ZSUyMGNhc3VhbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDgwOTYzMXww&ixlib=rb-4.1.0&q=85",
    bio: "High-school science teacher exploring AI in the classroom, thoughtfully.",
    categories: ["AI for Teaching & Learning", "Prompt Engineering"],
    tools: [
      { name: "ChatGPT", level: "Advanced" },
      { name: "Perplexity", level: "Intermediate" },
      { name: "ElevenLabs", level: "Beginner" },
    ],
    problemsPosted: 2,
    problems: [
      {
        title: "Personalised tutoring for a class of 32 students",
        description: "Designed AI worksheets that adapt to reading level. Every student gets the same core concept at the right difficulty.",
        outcome: "Average quiz scores +14%; struggling readers up 22%.",
        tools: ["ChatGPT"],
        categories: ["AI for Teaching & Learning"],
        dateSolved: "2025-11-22",
      },
      {
        title: "Cut grading time by 5 hours a week",
        description: "AI drafts rubric-based feedback; I review and personalise the top and bottom 20% in depth.",
        outcome: "Reclaimed a full evening per week; feedback consistency improved.",
        tools: ["ChatGPT"],
        categories: ["AI for Teaching & Learning"],
        dateSolved: "2025-05-30",
      },
    ],
  }),
  mk("aisha-mensah", {
    name: "Aisha Mensah", role: "Creative Director", location: "Accra, GH",
    photo: "https://images.pexels.com/photos/14251862/pexels-photo-14251862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    bio: "I direct campaigns for African-first brands. AI is my sketchpad; humans still make the calls.",
    categories: ["Generative Media", "Content & Creative AI"],
    tools: [
      { name: "Midjourney", level: "Expert" },
      { name: "Runway", level: "Advanced" },
      { name: "Sora", level: "Intermediate" },
    ],
    problemsPosted: 0,
    problems: [
      {
        title: "Delivered a national campaign 4 weeks ahead of schedule",
        description: "Pitched, moodboarded and generated key visuals with Midjourney before a single photographer was booked.",
        outcome: "Launched 4 weeks early; production budget -30%.",
        tools: ["Midjourney"],
        categories: ["Generative Media"],
        dateSolved: "2025-10-28",
      },
      {
        title: "Made 30 localised ad variants in a single afternoon",
        description: "Combined generative image + copy prompts to test creative angles across 6 markets.",
        outcome: "CTR +41% on the best-performing variant vs. control.",
        tools: ["Midjourney", "Runway"],
        categories: ["Generative Media", "Content & Creative AI"],
        dateSolved: "2025-08-14",
      },
    ],
  }),
];

// Total count of a person's listed solutions.
export const getSolutionCount = (person) => person.problems.length;
// Back-compat alias (older imports may still reference this name).
export const getVerifiedSolutions = getSolutionCount;

export const findPerson = (id) => PEOPLE.find((p) => p.id === id);
